import React from 'react';
import { HashRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from './components/utility/ErrorBoundary';
import { LazyLoader } from './components/utility/LazyLoader';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { Layout } from './components/Layout';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Login from './pages/Login';
import { useMenuItems } from './config/menuItems';
import { RotateCw } from 'lucide-react';

export const App: React.FC = () => {
  return (
    <div className={``}>
      <ErrorBoundary>
        <Router>
          <AuthProvider>
            <ToastProvider>
              <ThemeProvider>
                <LazyLoader>
                  <Routes>
                    <Route path='/' element={<Login />} />
                    <Route path='/*' element={<AuthenticatedRoute />} />
                  </Routes>
                </LazyLoader>
              </ThemeProvider>
            </ToastProvider>
          </AuthProvider>
        </Router>
      </ErrorBoundary>
    </div>
  );
};

const AuthenticatedRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <MenuLoaderLayout /> : <Navigate to='/login' replace />;
};

// Loads userId from context or localStorage, fetches menu via POST, and passes to Layout
const MenuLoaderLayout: React.FC = () => {
  const { user }: any = useAuth();
  const authUserId = user?.authUserId || localStorage.getItem('authUserId') || '';

  const { items: menu, loading, error, refetch } = useMenuItems(authUserId);

  if (loading) {
    return <div className='p-4 text-sm opacity-70'>Loading menu…</div>;
  }

  if (error) {
    return (
      <div className='p-6 text-sm bg-gray-50 border border-gray-200 rounded-xl text-center'>
        <p className='text-gray-700 mb-4'>Something Went wrong. Please try again later.</p>

        <button
          onClick={refetch}
          className='inline-flex items-center gap-2 bg-emerald-600 text-white font-medium px-5 py-2.5 rounded-lg
               hover:bg-emerald-700 transition-colors duration-200'
        >
          <RotateCw className='w-4 h-4' />
          Retry
        </button>
      </div>
    );
  }

  return <Layout menu={menu} />;
};
